import { logger } from "../utils/logger";
import { secureStorage } from "../utils/secureStorage";
/**
 * Session Configuration
 *
 * Centralized configuration for session management, warnings, and activity monitoring.
 */
export interface SessionConfig {
    /** Warning thresholds in milliseconds */
    warningThresholds: {
        /** Primary warning threshold (default: 5 minutes) */
        primary: number;
        /** Critical warning threshold (default: 1 minute) */
        critical: number;
        /** Final warning threshold (default: 30 seconds) */
        final: number;
    };
    /** Interval to check session status (milliseconds) */
    checkInterval: number;
    /** Time of inactivity before considering user idle (milliseconds) */
    activityIdleTime: number;
    /** Minimum time between session refreshes (milliseconds) */
    refreshCooldown: number;
    /** Enable auto-refresh on user activity */
    autoRefreshEnabled: boolean;
    /** Show countdown in header when session is running low */
    showHeaderCountdown: boolean;
    /** Enable sound notifications for warnings */
    soundEnabled: boolean;
    /** Enable desktop notifications for warnings */
    notificationsEnabled: boolean;
}
export const SESSION_CONFIG: SessionConfig = {
    warningThresholds: {
        primary: 5 * 60 * 1000, // 5 minutes
        critical: 60 * 1000, // 1 minute
        final: 30 * 1000 // 30 seconds
    },
    checkInterval: 30000, // Check every 30 seconds
    activityIdleTime: 60000, // 1 minute idle time
    refreshCooldown: 60000, // 1 minute between refreshes
    autoRefreshEnabled: true, // Auto-refresh enabled by default
    showHeaderCountdown: true, // Show header countdown by default
    soundEnabled: false, // Sound disabled by default
    notificationsEnabled: false // Desktop notifications disabled by default
};
/**
 * Get user session preferences from localStorage
 */
export async function getUserSessionPreferences(): Promise<Partial<SessionConfig>> {
    try {
        const stored = await secureStorage.get('apt_session_preferences');
        if (stored) {
            return stored;
        }
    }
    catch (error) {
        logger.auth('Failed to load session preferences:', { "arg0": error });
    }
    return {};
}
/**
 * Save user session preferences to localStorage
 */
export async function saveUserSessionPreferences(preferences: Partial<SessionConfig>): Promise<void> {
    try {
        await secureStorage.set('apt_session_preferences', preferences);
    }
    catch (error) {
        logger.auth('Failed to save session preferences:', { "arg0": error });
    }
}
/**
 * Get merged configuration (defaults + user preferences)
 */
export async function getSessionConfig(): Promise<SessionConfig> {
    const userPrefs = await getUserSessionPreferences();
    return {
        ...SESSION_CONFIG,
        ...userPrefs,
        warningThresholds: {
            ...SESSION_CONFIG.warningThresholds,
            ...(userPrefs.warningThresholds || {})
        }
    };
}
export default SESSION_CONFIG;
